import { useMemo } from "react"
import type { DocumentSummary } from "modules/permit"
import type { UploadedFileItem } from "modules/permit/components/RequiredDocumentsSection"
import type { TemplateConfiguration } from "shared/api/templateService"

interface UseRequiredDocumentsParams {
  templateConfig: TemplateConfiguration | null
  selectedActivities: string[]
  availableDocuments: DocumentSummary[]
  uploadedFiles: UploadedFileItem[]
}

export type RequiredDocumentItem = {
  id: string
  name: string
  code: string
  isMandatory: boolean
}

export const useRequiredDocuments = ({
  templateConfig,
  selectedActivities,
  availableDocuments,
  uploadedFiles,
}: UseRequiredDocumentsParams): RequiredDocumentItem[] =>
  useMemo(() => {
    if (templateConfig?.attachmentEnabled === false) {
      return []
    }

    const docMap = new Map<string, RequiredDocumentItem>()
    const isSubactivityEnabled = templateConfig?.subactivityEnabled !== false

    const addDoc = (params: {
      id?: string | null
      name?: string | null
      code?: string | null
      isMandatory?: boolean
    }) => {
      const docId = params.id?.trim()
      if (!docId) {
        return
      }

      const existing = docMap.get(docId)
      const resolvedName =
        params.name?.trim() ||
        existing?.name ||
        params.code?.trim() ||
        existing?.code ||
        docId
      const resolvedCode = params.code?.trim() || existing?.code || docId
      const isMandatory = existing?.isMandatory || Boolean(params.isMandatory)

      docMap.set(docId, {
        id: docId,
        name: resolvedName,
        code: resolvedCode,
        isMandatory,
      })
    }

    templateConfig?.requiredDocuments?.forEach((doc) => {
      addDoc({
        id: doc.documentId ?? doc.requiredDocId,
        name: doc.documentName,
        code:
          availableDocuments.find((summary) => summary.documentId === doc.documentId)?.code ??
          doc.documentName ??
          doc.documentId ??
          doc.requiredDocId,
        isMandatory: doc.isMandatory,
      })
    })

    if (isSubactivityEnabled) {
      templateConfig?.subActivities?.forEach((subactivity) => {
        if (!selectedActivities.includes(subactivity.subactivityId)) {
          return
        }
        subactivity.requiredDocuments?.forEach((doc) => {
          addDoc({
            id: doc.documentId ?? doc.requiredDocId,
            name: doc.documentName,
            code:
              availableDocuments.find((summary) => summary.documentId === doc.documentId)?.code ??
              doc.documentName ??
              doc.documentId ??
              doc.requiredDocId,
            isMandatory: doc.isMandatory,
          })
        })
      })
    }

    uploadedFiles.forEach((file) => {
      if (!file?.docId) {
        return
      }
      const summary = availableDocuments.find(
        (doc) =>
          doc.documentId === file.docId ||
          (file.documentCode && doc.code === file.documentCode),
      )
      addDoc({
        id: file.docId,
        name: summary?.name,
        code: summary?.code ?? file.documentCode ?? file.fileName ?? file.docId,
        isMandatory: false,
      })
    })

    return Array.from(docMap.values())
  }, [templateConfig, selectedActivities, availableDocuments, uploadedFiles])
